/**
 * Player stats window helpers.
 *
 * The FA page renders a 2-row-per-player UX (season row + rolling comparison row).
 * The user's dateRange selection chooses which slice the comparison row pulls
 * from AND which slice client-side sortValue functions use (so client sort
 * aligns with server ORDER BY).
 *
 * Rolling-row VISIBILITY is controlled by a separate "Recent" toggle in the
 * search-controls row — NOT by dateRange. See VirtualPlayerTable.js /
 * MobilePlayerTable.js showRecent prop.
 *
 * Backend emits stats.season + stats.rolling_7_day + stats.rolling_14_day +
 * stats.rolling_30_day on every FA response, plus stats.custom_window when the
 * client sent dateRange='custom' with start/end dates.
 *
 * - getRollingStatsForWindow: returns the slice for the rolling/comparison row.
 *   Default 'season' resolves to rolling_14_day (legacy semantic — the rolling
 *   row is always 14-day until the user explicitly picks another window).
 *
 * - getSortStatsForWindow: returns the slice for client-side sort comparators.
 *   Returns season slice when dateRange='season' so client sort aligns with the
 *   server-side ORDER BY (which sorts by season stats in the default window).
 *
 * - getRollingRowLabel / STATS_KEY_FOR_RANGE / ROLLING_LABEL_FOR_RANGE: small
 *   utilities for label rendering and slice lookup.
 *
 * Per NO FALLBACKS: invalid dateRange throws (programming error). Missing player
 * stats returns null (legitimate empty state).
 */

export const STATS_KEY_FOR_RANGE = Object.freeze({
  season:  'season',
  last7:   'rolling_7_day',
  last14:  'rolling_14_day',
  last30:  'rolling_30_day',
  custom:  'custom_window',
});

export const ROLLING_LABEL_FOR_RANGE = Object.freeze({
  season:  'Last 14',
  last7:   'Last 7',
  last14:  'Last 14',
  last30:  'Last 30',
  custom:  'Custom',
});

function assertValidRange(dateRange) {
  if (!(dateRange in STATS_KEY_FOR_RANGE)) {
    throw new Error(
      `Invalid dateRange '${dateRange}'. Expected one of ${Object.keys(STATS_KEY_FOR_RANGE).join(', ')}.`
    );
  }
}

/**
 * Rolling/comparison row's stats slice for the active window.
 * Default 'season' window: returns rolling_14_day (legacy semantic, the rolling
 * row is always 14-day until the user explicitly picks a different window).
 */
export function getRollingStatsForWindow(player, dateRange = 'season') {
  if (!player?.stats) return null;
  assertValidRange(dateRange);
  if (dateRange === 'season') return player.stats.rolling_14_day ?? null;
  const key = STATS_KEY_FOR_RANGE[dateRange];
  return player.stats[key] ?? null;
}

/**
 * Stats slice that client-side sortValue functions should consume.
 * Aligns client column-click sort with server's window-aware ORDER BY.
 * For 'season' returns the season slice (server sorts by season in default
 * window); for non-season windows returns the corresponding rolling slot.
 */
export function getSortStatsForWindow(player, dateRange = 'season') {
  if (!player?.stats) return null;
  assertValidRange(dateRange);
  const key = STATS_KEY_FOR_RANGE[dateRange];
  return player.stats[key] ?? null;
}

/** Rolling row label for the active dateRange. */
export function getRollingRowLabel(dateRange) {
  return ROLLING_LABEL_FOR_RANGE[dateRange] ?? null;
}
